import isObject from '#shared/util/isObject.js';

import cBancontact from '#themes/default/default/components/bancontact/bancontact.js';
import cBoleto from '#themes/default/default/components/boleto/boleto.js';
import cCreditCard from '#themes/default/default/components/creditCard/creditCard.js';
import cDirectDebit from '#themes/default/default/components/directDebit/directDebit.js';
import cInfoBox from '#themes/default/default/components/infoBox/infoBox.js';
import cIDeal from '#themes/default/default/components/iDeal/iDeal.js';
import cPaymentMethod from '#themes/default/default/components/paymentMethod/paymentMethod.js';
import cPayPalBilling from '#themes/default/default/components/payPalBilling/payPalBilling.js';
import cSvg from '#themes/default/default/components/svg/svg.js';
import cSofort from '#themes/default/default/components/sofort/sofort.js';
import cWireTransfer from '#themes/default/default/components/wireTransfer/wireTransfer.js';

import template from './paymentMethods.html' with { type: 'html' };

export default {
	name: 'CPaymentMethods',
	components: {
		cBancontact,
		cBoleto,
		cCreditCard,
		cDirectDebit,
		cIDeal,
		cInfoBox,
		cPaymentMethod,
		cPayPalBilling,
		cSofort,
		cSvg,
		cWireTransfer
	},
	props: {
		isPreselection: {
			type: Boolean,
			default: false
		},
		utils: {
			type: Object,
			default: () => null
		}
	},
	data() {
		return {
			value: '',
			newCartCookieName: 'js-newCart',
			onlyLogoPaymentMethods: ['payPalBilling', 'directDebit'],
			enabledPaymentMethods: []
		};
	},
	computed: {
		app() {
			return this.$store.state.app;
		},
		customer() {
			return this.$store.state.customer;
		},
		form() {
			return this.$store.state.form;
		},
		isCompany() {
			return this.$store.state.isCompany;
		},
		isEmpty() {
			return this.$store.state.isEmpty;
		},
		isLoading() {
			return this.$store.state.isLoading;
		},
		isNewMethod() {
			return this.$store.state.isNewMethod;
		},
		isNewCart() {
			return this.$store.state.isNewCart;
		},
		order() {
			return this.$store.state.order;
		},
		page() {
			return this.$store.state.page;
		},
		payment() {
			return this.$store.state.payment;
		},
		selectedPayment() {
			return this.$store.state.selectedPayment;
		},
		providerAccount() {
			return this.$store.state.providerAccount;
		},
		theme() {
			return this.$store.state.theme;
		},
		url() {
			return this.$store.state.url;
		},
		providerElementsLoaded() {
			return this.$store.getters.providerElementsLoaded;
		},
		isShoppingCartPage() {
			return isObject(this.page) && this.page.number === 0;
		},
		isPaymentDetailsPage() {
			return isObject(this.page) && this.page.number === 1 && this.page.name === 'Payment Details';
		},
		isCreditCard() {
			return this.value === 'creditCard';
		},
		isPayPalBilling() {
			return this.value === 'payPalBilling';
		},
		isBoleto() {
			return this.value === 'boleto';
		},
		isBuyingForCompanyEnabled() {
			return this.order.isGuest && this.selectedPayment.isCompany && !this.isPreselection && !this.order.hasBusinessProduct && !this.order.customIDs.includes('BFCOFF') && !this.order.isBrazilianFlow;
		},
		isNotRecurringMethod() {
			// Not recurring methods in Brazil display another payment note
			return this.order.isBrazilianFlow && this.isBoleto;
		},
		getSubmitTextKey() {
			if (this.isCustomId['CART-2967']) {
				return 'cart_subscribe';
			}
			return 'cart_submit';
		},
		paymentSourceOwner() {
			return {
				firstName: this.customer.billing.firstName,
				lastName: this.customer.billing.lastName,
				email: this.order.isGuest ? this.customer.billing.email : this.providerAccount.email,
				country: this.customer.billing.country,
				city: this.customer.billing.city,
				provinceState: this.customer.billing.state,
				postalCode: this.customer.billing.zip,
				companyName: this.isCompany && this.customer.billing.companyName ? this.customer.billing.companyName : '',
				addressLine1: this.isCompany && this.customer.billing.line1 ? this.customer.billing.line1 : ''
			};
		},
		show() {
			return this.isShoppingCartPage && !this.order.isBrazilianFlow || this.isPaymentDetailsPage;
		}
	},
	created() {
		// Select a default payment method immediately to have a visible form during loading
		this.selectDefaultPaymentMethod();
	},
	async mounted() {
		// Wait until the cart is fully loaded, then filter/select correct payment method - e.g. Trials
		await this.waitUntil([() => !this.isEmpty], 60 * 1000);
		// Determine if payment method preselection should be shown for trial products (eg. when coming back from PayPal)
		const shouldShowPreselection = this.order.id !== this.cookieStorage.getItem(this.newCartCookieName);
		this.$store.commit('SET_NEW_CART', shouldShowPreselection);
	},
	methods: {
		onChange(selectedPaymentMethodId) {
			this.value = selectedPaymentMethodId;
			// Update selected payment method in Vuex store
			this.$store.commit('SET_SELECTED_PAYMENT', {
				type: selectedPaymentMethodId
			});
			// Set initial provider elements state to show loaders until ready
			if (IS_CLIENT) {
				this.$store.commit('SET_SELECTED_PAYMENT', {
					cardBrand: null,
					cardcvv: null,
					cardexpiration: null,
					cardnumber: null,
					iban: null
				});
				// Reset form so there are no visible errors when changing the payment methods
				this.$store.commit('SET_FORM', {
					creditCard: null,
					directDebit: null
				});
				// Update provider library for the selected payment method (eg. change UI for creditCard or directDebit)
				this.providersLibrary.loadPayment();
			}
		},
		getPaymentSource() {
			// Payment source creation options
			const options = {
				paymentType: this.selectedPayment.type, // Required for provider
				type: this.selectedPayment.type // Required for checkout
			};
			// Query parameters to be removed from the URLs for "redirect" payment methods
			const ignoredQuery = {
				afbData: null,
				aswparam: null,
				cid: null,
				myAppContext: null,
				rtprice: null
			};
			if (['iDeal', 'sofort'].includes(options.paymentType)) {
				const returnUrl = this.getLink('thank-you', null);
				const cancelUrl = this.getLink('shopping-cart', ignoredQuery);
				options.returnUrl = `${this.url.origin}${returnUrl}`;
				options.cancelUrl = `${this.url.origin}${cancelUrl}`;
			}
			if (['payPalBilling'].includes(options.paymentType)) {
				const productName = this.order.products.map((product) => product.name).join(', ');
				const returnUrl = this.getLink('confirm-order', ignoredQuery);
				const cancelUrl = this.getLink('shopping-cart', ignoredQuery);
				window.loadPaypal({
					returnURL: `${this.url.origin}${returnUrl}`,
					cancelURL: `${this.url.origin}${cancelUrl}`,
					totalPrice: this.order.price.total.unformatted,
					productName
				});
			}
			if (['bancontact', 'creditCard', 'directDebit'].includes(options.paymentType)) {
				window.createWallet(this.paymentSourceOwner);
			}
			return this.selectedPayment.isLibrarySkippable ? options : this.providersLibrary.catchPaymentSource(options);
		},
		selectDefaultPaymentMethod() {
			this.enabledPaymentMethods = this.payment.paymentMethods;
			// Filter available payment methods for trial products
			if (this.order.hasTrialProduct) {
				this.enabledPaymentMethods = this.enabledPaymentMethods.filter((paymentMethod) => paymentMethod.isTrial);
			}
			// Hotfix: Remove PayPal from the payment tabs for 1-click trial product flow (CART-4866)
			if (this.order.hasTrialProduct && this.order.isAuthenticated) {
				this.enabledPaymentMethods = this.enabledPaymentMethods.filter((paymentOption) => paymentOption.id !== 'payPalBilling');
			}
			// Filter OMS flow payment methods
			if (this.order.isOmsFlow) {
				this.enabledPaymentMethods = this.enabledPaymentMethods.filter((paymentMethod) => paymentMethod.isOms);
			}
			// Filter manual renewal methods from 1-click new method flow
			if (this.isNewMethod) {
				this.enabledPaymentMethods = this.enabledPaymentMethods.filter((paymentMethod) => !paymentMethod.isManualRenewal);
			}
			const [defaultPaymentMethod] = this.enabledPaymentMethods;
			const foundPaymentMethod = this.enabledPaymentMethods.find((paymentMethod) => paymentMethod.id === this.payment.name);
			this.onChange(foundPaymentMethod ? foundPaymentMethod.id : defaultPaymentMethod.id);
		},
		toggleIsCompany() {
			this.tracking.buyingForACompany({
				order: this.order,
				isCompany: !this.isCompany
			});
			this.$store.commit('SET_COMPANY', !this.isCompany);
		}
	},
	template
};
